// ===========================================
// Registration Status Line
// ===========================================
// The lifetime (never year-filtered) registration-fee status shown at the top
// of a family's Payment History: PAID, WAIVED, or NONE — the word "Unpaid"
// never appears (md/plan/family-payment-history.md Task 4.2, L6).

import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { FamilyPaymentHistory, FamilyPaymentRow } from '../../../api/familyPayments.api';
import { PAYMENT_METHOD_LABELS } from '../../../utils/invoiceDisplay';
import { formatPaymentDay } from '../../../utils/invoiceDisplay';
import { formatCurrencyFromDollars } from '../../../utils/currency';

interface RegistrationStatusLineProps {
  registration: FamilyPaymentHistory['registration'];
  timeZone: string;
  canWrite: boolean;
  canDelete: boolean;
  onRecord: () => void;
  onWaive: () => void;
  onEditPayment: (payment: FamilyPaymentRow) => void;
  onDeletePayment: (payment: FamilyPaymentRow) => void;
  onRevokeWaiver: () => void;
  /** #374: the fees list shows the paid fee(s), so the one-line Paid summary is hidden. */
  hidePaidLine?: boolean;
  /** #374 PER_STUDENT: a further child still needs a fee recorded. */
  showRecordMore?: boolean;
}

// 44px touch target on phones, matching the invoice Payment History pattern.
const iconButtonSx = { p: { xs: 1.5, sm: 0.5 }, minWidth: { xs: 44 }, minHeight: { xs: 44 } };

export default function RegistrationStatusLine({
  registration,
  timeZone,
  canWrite,
  canDelete,
  onRecord,
  onWaive,
  onEditPayment,
  onDeletePayment,
  onRevokeWaiver,
  hidePaidLine = false,
  showRecordMore = false,
}: RegistrationStatusLineProps) {
  const { status, payment, waiver, legacyAdminAttested } = registration;

  const recordMoreButton = showRecordMore && canWrite && (
    <Button size="small" onClick={onRecord} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
      Record registration payment
    </Button>
  );

  if (status === 'PAID' && hidePaidLine) {
    return recordMoreButton ? <Box>{recordMoreButton}</Box> : null;
  }

  if (status === 'PAID' && payment) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2">
          <strong>Registration fee:</strong> Paid {formatPaymentDay(payment.paidAt, timeZone)} ·{' '}
          {PAYMENT_METHOD_LABELS[payment.method]} · {formatCurrencyFromDollars(payment.amount)}
          {payment.isManual ? ' · Recorded by admin' : ''}
        </Typography>
        {recordMoreButton}
        {payment.isManual && (canWrite || canDelete) && (
          <Stack direction="row" spacing={0.5}>
            {canWrite && (
              <IconButton
                size="small"
                aria-label="Edit registration payment"
                sx={iconButtonSx}
                onClick={() => onEditPayment(payment)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                size="small"
                aria-label="Delete registration payment"
                sx={iconButtonSx}
                onClick={() => onDeletePayment(payment)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>
    );
  }

  if (status === 'WAIVED' && waiver) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2">
          <strong>Registration fee:</strong> Waived {formatPaymentDay(waiver.waivedAt, timeZone)}
          {waiver.waivedByName ? ` by ${waiver.waivedByName}` : ''} — {waiver.reason}
        </Typography>
        {canWrite && (
          <Button size="small" onClick={onRevokeWaiver} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
            Revoke waiver
          </Button>
        )}
      </Box>
    );
  }

  // NONE
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary">
        {legacyAdminAttested
          ? 'No registration payment recorded. Converted by an admin, who confirmed the fee was collected outside the system.'
          : 'No registration payment on record'}
      </Typography>
      {canWrite && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button size="small" onClick={onRecord} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
            Record registration payment
          </Button>
          <Button size="small" onClick={onWaive} sx={{ whiteSpace: 'nowrap', minHeight: { xs: 44 } }}>
            Waive fee
          </Button>
        </Stack>
      )}
    </Box>
  );
}
